import { KeyValuePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ParentAiService } from '../../parent-ai.service';
import {
  formatTimeAgo as formatTimeAgoUtil,
  getCategoryIcon,
  getCategoryLabelKey,
  getSeverityColor,
} from '../../constants/topic-category.constants';
import { UserService } from '../../../user/service/user.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { ai as AiGen } from '@valvur/shared-grpc';
import type { ChildOption } from '../../models/child-option.model';
import { RobocatMascotModelComponent } from '../robocat-mascot-model/robocat-mascot-model.component';
import { GaEventService } from '../../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../../../../core/analytics/parent-analytics.events';

@Component({
  selector: 'app-ai-assistant-header',
  standalone: true,
  imports: [KeyValuePipe, TranslatePipe, RobocatMascotModelComponent],
  templateUrl: './ai-assistant-header.component.html',
  styleUrl: './ai-assistant-header.component.scss',
})
export class AiAssistantHeaderComponent {
  #router = inject(Router);
  #parentAi = inject(ParentAiService);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #translate = inject(TranslateService);
  #analytics = inject(GaEventService);

  showTopicsPanel = signal(false);

  user = computed(() => this.#userService.user());
  children = computed<ChildOption[]>(() =>
    this.#familyKids.toChildOptions(
      this.#translate.instant('app.parentPatrol.labels.childFallback'),
    ),
  );
  selectedChildId = this.#parentAi.selectedChildId;
  selectedChild = computed(() => {
    const id = this.selectedChildId();
    if (!id) return null;
    return this.children().find((c) => c.id === id) ?? null;
  });
  isVerificationMode = computed(() => {
    if (this.user() == null) return false;
    return !this.#familyKids.hasChildren();
  });

  topics = computed(() => this.#parentAi.topics());
  selectedTopicId = this.#parentAi.selectedTopicId;
  selectedTopic = computed(() => {
    const id = this.selectedTopicId();
    if (!id) return null;
    return this.topics().find((t) => t.id === id) ?? null;
  });
  filteredTopics = computed(() => this.topics());
  topicCounts = computed(() => {
    const counts: Record<string, number> = {};
    for (const t of this.filteredTopics()) {
      const key = t.primaryTopic ?? 'general';
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  });
  isLoadingTopics = computed(() => this.#parentAi.topicsQuery.isPending());
  isNeutralChildScope = computed(() => this.#parentAi.isNeutralParentingScope());

  constructor() {
    effect(() => {
      this.#parentAi.isVerificationMode.set(this.isVerificationMode());
    });
  }

  goToAiAssistant(): void {
    this.#analytics.sendEvent(ParentAnalyticsEvents.OPEN_AI_ASSISTANT, {
      layout: PARENT_ANALYTICS_LAYOUT,
    });
    void this.#router.navigate(['/ai-assistant']);
  }

  toggleTopicsPanel(): void {
    if (this.isNeutralChildScope()) return;
    this.showTopicsPanel.update((v) => !v);
  }

  selectTopic(topic: AiGen.TopicThreadDto): void {
    this.#parentAi.selectedTopicId.set(topic.id);
    this.showTopicsPanel.set(false);
  }

  startNewConversation(): void {
    this.#parentAi.startNewConversation();
    this.showTopicsPanel.set(false);
  }

  getCategoryLabelKey = getCategoryLabelKey;
  getCategoryIcon = getCategoryIcon;
  getSeverityColor = getSeverityColor;

  formatTimeAgo(ts: number): string {
    return formatTimeAgoUtil(ts, (key, params) =>
      this.#translate.instant(key, params),
    );
  }
}
